import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ViewReport from "../components/reportsPage/viewCustomButton";
import EditUser from "../components/userForm/editUser";
import AddUser from "../components/userForm/addUser";
import TableSkeleton from "../components/common/tableSkeleton";
import ErrorDisplay from "../components/common/error";
import {
  getUsers,
  resetAddUser,
  resetEditUser,
} from "../redux/users/usersSlice";
import { filterUsers, findRoleName } from "../utils/filterUsers";
import CustomInput from "../components/common/customInput";
import { useTranslation } from "react-i18next";
import { FaQuestion } from "react-icons/fa6";
import { LuEye, LuEyeOff } from "react-icons/lu";
import useBranchGuard from "../hooks/useBranchGuard";

const MASKED_PIN = "••••";

const UserActions = () => {
  const { t } = useTranslation();

  const branchUrl = useParams().id;
  const dispatch = useDispatch();
  useBranchGuard();

  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isHovered, setIsHovered] = useState({});
  const [visiblePins, setVisiblePins] = useState({});

  const handleClick = (user) => {
    setSelected(user);
    setOpen(true);
  };

  useEffect(() => {
    dispatch(getUsers({ branchUrl }));
  }, [dispatch, branchUrl]);

  const usersReducer = useSelector((state) => state.users);
  const {
    loading: usersLoading,
    success: usersSuccess,
    error: usersError,
    users,
  } = usersReducer.getUsers;

  useEffect(() => {
    return () => {
      dispatch(resetAddUser());
      dispatch(resetEditUser());
    };
  });
  const filteredUsers = filterUsers(users, searchQuery);

  const setHover = (id, value) => {
    setIsHovered((prev) => ({ ...prev, [id]: value }));
  };

  const togglePin = (id) => {
    setVisiblePins((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (open || openAdd) {
      document.body.classList.add("body_overflow_hidden");
    } else {
      document.body.classList.remove("body_overflow_hidden");
    }

    return () => {
      document.body.classList.remove("body_overflow_hidden");
    };
  }, [open, openAdd]);

  return (
    <section className="min-h-[100vh] flex flex-col items-center">
      <main className="w-full md:w-[40rem]">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center">
          <div className="sm:mr-2 md:w-full md:max-w-80">
            <CustomInput
              type="text"
              placeholder={t("nav.search")}
              ariaLabel={t("nav.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full left-0 mb-2 p-3 rounded text-[--gr-4] text-sm border border-[--gr-2] border-solid cursor-text"
            />
          </div>
          {usersSuccess && (
            <button
              type="button"
              onClick={() => setOpenAdd(true)}
              className="text-white bg-[--primary] hover:bg-[--primary-hover] justify-center font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center"
            >
              <svg
                className="w-5 h-5 me-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 18 21"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {t("userMgmnt.add")}
            </button>
          )}
        </div>
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg mb-8">
          {usersSuccess ? (
            <>
              {filteredUsers.length > 0 ? (
                <table className="w-full text-sm text-left rtl:text-right text-[--gr-5]">
                  <thead className="text-xs text-[--gr-7] uppercase bg-[--gr-0]">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-6 font-medium text-lg normal-case"
                      >
                        {t("userMgmnt.name")}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-6 font-medium text-lg normal-case"
                      >
                        {t("userMgmnt.pass")}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-6 font-medium text-lg normal-case"
                      >
                        {t("userMgmnt.role")}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-6 font-medium text-lg text-right normal-case"
                      >
                        {t("userMgmnt.act")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredUsers.map((entity) => (
                      <tr
                        key={entity.id}
                        className="odd:bg-[--btn-txt] even:bg-[--gr-0]"
                      >
                        <th
                          scope="row"
                          className="px-6 py-2 font-medium text-[--gr-9] whitespace-nowrap"
                        >
                          {entity.name}
                        </th>
                        <td className="px-6 py-2 font-medium text-[--gr-9] whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <span className="font-mono tracking-widest min-w-[4ch]">
                              {visiblePins[entity.id]
                                ? entity.pinCode
                                : MASKED_PIN}
                            </span>
                            <button
                              type="button"
                              onClick={() => togglePin(entity.id)}
                              aria-label={
                                visiblePins[entity.id]
                                  ? t("common.hidePin")
                                  : t("common.showPin")
                              }
                              aria-pressed={Boolean(visiblePins[entity.id])}
                              className="text-lg text-[--tx-3] hover:text-[--primary] bg-transparent flex items-center"
                            >
                              {visiblePins[entity.id] ? (
                                <LuEyeOff aria-hidden="true" />
                              ) : (
                                <LuEye aria-hidden="true" />
                              )}
                            </button>
                          </span>
                        </td>
                        <td className="px-6 py-2 font-medium text-[--gr-9] whitespace-nowrap">
                          {findRoleName(entity.userRoleId, users)}
                        </td>
                        <td className="px-6 py-2 text-right">
                          {entity.isEditable ? (
                            <ViewReport
                              onClick={() => handleClick(entity)}
                              text={t("userMgmnt.edit")}
                            />
                          ) : (
                            <div className="flex justify-end relative">
                              <button
                                type="button"
                                aria-label={t("userMgmnt.msg")}
                                className="cursor-help rounded-full border border-[--gr-9] border-solid p-1 text-[--gr-9] bg-transparent flex items-center"
                                onMouseEnter={() => setHover(entity.id, true)}
                                onMouseLeave={() => setHover(entity.id, false)}
                                onFocus={() => setHover(entity.id, true)}
                                onBlur={() => setHover(entity.id, false)}
                              >
                                <FaQuestion aria-hidden="true" />
                              </button>
                              <div
                                role="tooltip"
                                className={`py-3 px-4 bg-[--gr-2] text-[--gr-9] text-sm w-max max-w-[16rem] whitespace-normal text-left absolute -top-1 right-8 rounded-md shadow z-10 ${
                                  isHovered[entity.id] ? "visible" : "invisible"
                                }`}
                              >
                                {t("userMgmnt.msg")}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <ErrorDisplay error={t("errors.noResults")} />
              )}
              <AddUser
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                roles={users.userRoles}
                users={users.users}
              />
              <EditUser
                open={open}
                onClose={() => setOpen(false)}
                roles={users.userRoles}
                user={selected}
                users={users.users}
              />
            </>
          ) : usersLoading ? (
            <TableSkeleton />
          ) : usersError ? (
            <ErrorDisplay
              error={usersError}
              onRetry={() => dispatch(getUsers({ branchUrl }))}
            />
          ) : null}
        </div>
      </main>
    </section>
  );
};

export default UserActions;
